import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FilterFileResolutionRequest, GettingFileResolutionDto } from 'src/generated/services/TFinancialApi';
import { useGetDomainMetaData } from 'src/use-cases/invoice/useInvoices';
import { IInternalDashboardFilterParams, IInternalDashboardValues, ROW_ACTION_TYPE, SORTING_DIRECTION, TTableType } from './types';

export interface IInternalDashboardSelectorProps {
  children: ReactNode;
}

// eslint-disable-next-line react-refresh/only-export-components
export const internalDashboardDefaultValues = {
  selectedRow: null,
  selectedRowType: null,
  rowActionType: null,
  isShowResolutionMessage: false,
  isShowErrorMessage: false,
  isShowUserMessage: false,
  isShowEditInvoiceData: false,
  isShowEditOcrData: false,
  isShowEditConfig: false,
  isShowResolve: false,
  isShowReprocessShipment: false,
  isActionLoading: false,
  isValidateInvoice: false,
  resolvedStatuses: [],
  unresolvedStatuses: [],

  filterParams: {
    page: 1,
    size: 25,
    fileResolutionStatuses: [],
    dateFrom: '',
    dateTo: '',
    tabName: 'ACTION_NEEDED',
    paramsIsNull: true,
    sortingColum: 'resolutionCreatedOn',
    sortingDirection: SORTING_DIRECTION.DESC,
  },

  runRowAction: () => {},
  stopRowAction: () => {},
  checkRowAction: () => false,
  setIsActionLoading: () => {},
  setFilterPage: () => {},
  setFilterStatuses: () => {},
  setFilterTabName: () => {},
  setSorting: () => {},
};

export const InternalDashboardSelectorContext = createContext<IInternalDashboardValues>(internalDashboardDefaultValues);

export const InternalDashboardSelector = ({ children }: IInternalDashboardSelectorProps) => {
  const [selectedRow, setSelectedRow] = useState<GettingFileResolutionDto | null>(null);
  const [isActionLoading, setIsActionLoading] = useState<boolean>(false);
  const [selectedRowType, setSelectedRowType] = useState<TTableType | null>(null);
  const [rowActionType, setRowActionType] = useState<ROW_ACTION_TYPE | null>(null);
  const [filterParams, setFilterParams] = useState<IInternalDashboardFilterParams>(internalDashboardDefaultValues.filterParams);
  const [resolvedStatuses, setResolvedStatuses] = useState<string[]>([]);
  const [unresolvedStatuses, setUnresolvedStatuses] = useState<string[]>([]);

  const { data } = useGetDomainMetaData();

  useEffect(() => {
    if (data?.unResolvedResolutionStatuses && data?.unResolvedResolutionStatuses?.length > 0) {
      setUnresolvedStatuses(data.unResolvedResolutionStatuses);
    }
    if (data?.resolvedResolutionStatuses && data?.resolvedResolutionStatuses?.length > 0) {
      setResolvedStatuses(data.resolvedResolutionStatuses);
    }
  }, [data]);

  const isShowResolutionMessage = useMemo(() => selectedRow && rowActionType === ROW_ACTION_TYPE.RESOLUTION_MESSAGE, [selectedRow, rowActionType]);
  const isShowErrorMessage = useMemo(() => selectedRow && rowActionType === ROW_ACTION_TYPE.ERROR_MESSAGE, [selectedRow, rowActionType]);
  const isShowUserMessage = useMemo(() => selectedRow && rowActionType === ROW_ACTION_TYPE.USER_MESSAGE, [selectedRow, rowActionType]);
  const isShowEditInvoiceData = useMemo(() => selectedRow && rowActionType === ROW_ACTION_TYPE.EDIT_INVOICE, [selectedRow, rowActionType]);
  const isShowEditOcrData = useMemo(() => selectedRow && rowActionType === ROW_ACTION_TYPE.EDIT_OCR, [selectedRow, rowActionType]);
  const isShowEditConfig = useMemo(() => selectedRow && rowActionType === ROW_ACTION_TYPE.EDIT_CONFIG, [selectedRow, rowActionType]);
  const isShowResolve = useMemo(() => selectedRow && rowActionType === ROW_ACTION_TYPE.RESOLVE, [selectedRow, rowActionType]);
  const isValidateInvoice = useMemo(() => selectedRow && rowActionType === ROW_ACTION_TYPE.VALIDATE_INVOICE, [selectedRow, rowActionType]);
  const isShowReprocessShipment = useMemo(() => selectedRow && rowActionType === ROW_ACTION_TYPE.REPROCESS_SHIPMENT, [selectedRow, rowActionType]);

  const runRowAction = useCallback((rowData: any, type: ROW_ACTION_TYPE, tableType: TTableType = 'file_resolution') => {
    setSelectedRow(rowData);
    if (tableType) {
      setSelectedRowType(tableType);
    }
    setRowActionType(type);
  }, []);

  const stopRowAction = useCallback(() => {
    setSelectedRow(null);
    setSelectedRowType(null);
    setRowActionType(null);
  }, []);

  const checkRowAction = useCallback((type: ROW_ACTION_TYPE) => selectedRow && rowActionType === type, [selectedRow, rowActionType]);

  const setFilterPage = useCallback((newPage: number) => {
    setFilterParams((currentData) => ({ ...currentData, page: newPage }));
  }, []);
  const setFilterTabName = useCallback((tabName: string) => {
    let sortingOptions = {};
    if (tabName === 'ALL_FILES') {
      sortingOptions = {
        sortingColum: 'createdOn',
        sortingDirection: SORTING_DIRECTION.DESC,
      };
    } else {
      sortingOptions = {
        sortingColum: 'resolutionCreatedOn',
        sortingDirection: SORTING_DIRECTION.DESC,
      };
    }
    setFilterParams((currentData) => ({ ...currentData, tabName, ...sortingOptions }));
  }, []);
  const setSorting = useCallback((fieldName: string, method: SORTING_DIRECTION) => {
    setFilterParams((currentData) => ({ ...currentData, sortingColum: fieldName, sortingDirection: method }));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setFilterStatuses = (statuses: FilterFileResolutionRequest['fileResolutionStatuses']) => {
    const allStatuses = [...resolvedStatuses, ...unresolvedStatuses];
    const isNull = allStatuses?.length === statuses.length || statuses?.length === 0;
    setFilterParams((currentData) => ({ ...currentData, fileResolutionStatuses: statuses, paramsIsNull: isNull }));
  };

  const value = useMemo(
    () => ({
      selectedRow,
      selectedRowType,
      rowActionType,
      isShowResolutionMessage,
      isShowErrorMessage,
      isShowUserMessage,
      isShowEditInvoiceData,
      isShowEditOcrData,
      isShowEditConfig,
      isShowResolve,
      isActionLoading,
      isValidateInvoice,
      isShowReprocessShipment,
      filterParams,
      resolvedStatuses,
      unresolvedStatuses,
      runRowAction,
      stopRowAction,
      checkRowAction,
      setIsActionLoading,
      setFilterPage,
      setFilterStatuses,
      setFilterTabName,
      setSorting,
    }),
    [
      selectedRow,
      selectedRowType,
      rowActionType,
      isShowResolutionMessage,
      isShowErrorMessage,
      isShowUserMessage,
      isShowEditInvoiceData,
      isShowEditOcrData,
      isShowEditConfig,
      isShowResolve,
      isActionLoading,
      isShowReprocessShipment,
      isValidateInvoice,
      filterParams,
      resolvedStatuses,
      unresolvedStatuses,
      runRowAction,
      stopRowAction,
      checkRowAction,
      setFilterPage,
      setFilterStatuses,
      setFilterTabName,
      setSorting,
    ]
  );

  return <InternalDashboardSelectorContext.Provider value={value}>{children}</InternalDashboardSelectorContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export function useInternalDashboardSelector() {
  const context = useContext(InternalDashboardSelectorContext);

  if (!context) {
    throw new Error(`Upload Invoices selector compound components cannot be rendered outside the Upload Invoices Selector component`);
  }
  return context;
}
